import { canEditLocation } from '../utils/rowCapabilities'
import type { BoxTruckVerification } from '../services/boxTruckLoadsApi'
import { BoxTruckLastLocationCell } from './BoxTruckLastLocationCell'

/** What the saved fix carries back, which is what the caller patches its row and its selection with. */
type SavedLocation = NonNullable<BoxTruckVerification['lastLocation']>

/** One header fact, in the same chip the registration meter uses so the strip reads as one row of facts. */
const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex min-w-0 items-center gap-2 rounded-xl border border-border bg-surface px-3 py-1.5">
        <span className="shrink-0 text-[11px] font-medium uppercase tracking-wider text-foreground-muted">{label}</span>
        <span className="min-w-0 truncate text-xs font-medium text-foreground">{children}</span>
    </div>
)

/**
 * The two facts a driver detail is checked for before anything else: where they are, and whose driver they
 * are.
 *
 * Both used to be fields inside the quick view, several rows down among two dozen others, which is the wrong
 * place for the answer somebody opens the screen already looking for — and for the location it was worse
 * than inconvenient: the table offers a correction right there in the row, so an operator who noticed a
 * stale fix from the DETAIL had to go back to the list to fix it. Here the same editor is one click away
 * from the same reading.
 *
 * The editor appears only when `onLocationSaved` is given AND the driver can carry a correction. A location
 * belongs to an ACCOUNT: for an accountless row `userId` is the fleet record id, the correction endpoint
 * resolves through live claims and answers 404 every time — so the control is withheld rather than offered
 * and guaranteed to fail. That is the same `canEditLocation` gate the list uses, asked in one place.
 *
 * An absent HR manager still shows, as "—". Which drivers nobody has taken on is a thing an operator needs
 * to be able to see, and a hidden field looks like a screen that does not carry it.
 */
export const DriverHeaderFacts = ({ driver, onLocationSaved }: { driver: BoxTruckVerification; onLocationSaved?: (location: SavedLocation) => void }) => (
    <>
        <Fact label="Last location">
            <BoxTruckLastLocationCell
                layout="inline"
                lastLocation={driver.lastLocation}
                profileLocation={driver.profileLocation}
                editable={onLocationSaved && canEditLocation(driver) ? { userId: driver.userId, onSaved: onLocationSaved } : undefined}
            />
        </Fact>
        <Fact label="HR Manager">{driver.hrManager?.name || '—'}</Fact>
    </>
)
